import i18n from 'i18next';
import HttpBackend from 'i18next-http-backend';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { languageDetectionOptions } from '@nexus/supabase';

// Bundled English source — committed at apps/ari-cloud/public/locales/en.json.
// The app still HTTP-loads other locales from /locales/{{lng}}.json, but
// English is bundled so we never render raw keys (and so unit tests don't need
// network or Lokalise creds to see real translations).
import enTranslations from '../../public/locales/en.json';

// `@nexus/ari-lib` imports trigger a top-level `i18next.init()` in
// `libs/ari-lib/src/locales/i18n-provider.tsx` with `defaultNS: 'common'` and a
// `resourcesToBackend` loader that pulls the lib's tiny legacy
// `langs/<lng>/common.json` (sidebar/demo strings only). Because ari-cloud
// imports from `@nexus/ari-lib` before importing this file, that lib init runs
// first and seats i18next on the wrong namespace, so top-level keys like
// `connectors.microsoft_contacts.description` resolve into the lib's `common`
// bundle and render as the raw key. The init below re-pins `defaultNS` to
// `translation` and overlays the full app bundle into both namespaces; the
// event listeners re-overlay after any backend load so the lib's loader can't
// shadow our keys.

const overlayEnBundle = () => {
  i18n.addResourceBundle('en', 'translation', enTranslations, true, true);
  i18n.addResourceBundle('en', 'common', enTranslations, true, true);
  if (i18n.options.defaultNS !== 'translation') {
    i18n.setDefaultNamespace('translation');
  }
};

i18n
  .use(HttpBackend)
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    fallbackLng: 'en',
    defaultNS: 'translation',
    ns: ['translation', 'common'],
    fallbackNS: ['translation', 'common'],
    supportedLngs: ['en', 'de', 'es', 'fr', 'it', 'ja', 'nl', 'pl', 'pt', 'tr', 'zh'],
    nonExplicitSupportedLngs: true,
    // English is bundled below (resources.en), but every other locale is
    // HTTP-loaded from /locales/{{lng}}.json. Without this flag i18next treats
    // the bundled resource store as complete and never asks the backend for a
    // detected non-English language at boot — so the pre-auth login screen
    // resolves to the 'en' fallback even when detection picks 'de' (CLOUD-559).
    // ari sets this for the same reason; keep both apps consistent.
    partialBundledLanguages: true,
    load: 'languageOnly', // Strips region codes (en-US -> en)
    debug: process.env.NODE_ENV === 'development',

    interpolation: {
      escapeValue: false,
    },

    resources: {
      en: { translation: enTranslations, common: enTranslations },
    },

    // English is bundled (above), so first paint never needs to wait for an
    // HTTP locale fetch. Suspense-on-load would block render until that fetch
    // resolves — pointless when resources are already present, and it makes
    // unit tests render an empty tree when the jsdom fetch is in flight.
    react: { useSuspense: false },

    detection: languageDetectionOptions,

    backend: {
      loadPath: '/locales/{{lng}}.json',
    },
  });

// Re-overlay after any (re-)init or backend fetch. Skip 'added' to avoid
// recursing on our own addResourceBundle calls.
i18n.on('initialized', overlayEnBundle);
i18n.on('languageChanged', overlayEnBundle);
i18n.on('loaded', overlayEnBundle);

// Cover the case where lib init already finished before this file ran: the
// 'initialized' event won't fire again, but the overlay still needs to land.
if (i18n.isInitialized) {
  overlayEnBundle();
}

export default i18n;
